import Color from 'color';

import type {ColorThemeFactory, ColorThemeFactoryOptions} from '.';

export const colorThemeV2Factory: ColorThemeFactory<ColorsV2> = (
  themeId,
  options,
) => {
  const shouldApplyAccentColor = !!options?.accentColor;

  switch (themeId) {
    case 'dark':
      return shouldApplyAccentColor
        ? applyAccentColor(darkV2, options)
        : darkV2;
    default:
      return shouldApplyAccentColor
        ? applyAccentColor(lightV2, options)
        : lightV2;
  }
};

const applyAccentColor = (
  colors: ColorsV2,
  options: ColorThemeFactoryOptions,
): ColorsV2 => {
  if (!options?.accentColor) {
    throw new Error('Accent color is required');
  }

  const accentColor = options.accentColor;
  const accentForSecondaryColors = options.accentForSecondaryColors || false;

  const result: ColorsV2 = {
    ...colors,
    background3: Color(accentColor).alpha(0.3).toString(),
    background4: Color(accentColor).alpha(0.1).toString(),
    background8: accentColor,
    border3: accentColor,
    button: {
      ...colors.button,
      border1: accentColor,
      fill1: accentColor,
      hover1: Color(accentColor).lighten(0.2).toString(),
      pressed1: Color(accentColor).lighten(0.2).toString(),
    },
    global1: accentColor,
    icon4: accentColor,
    lostColors: {
      ...colors.lostColors,
      greenElephant: Color(accentColor).lighten(0.2).toString(),
    },

    shadow1: Color(accentColor).lighten(0.1).alpha(0.4).toString(),

    text4: accentColor,
  };

  if (accentForSecondaryColors) {
    result.global2 = accentColor;
    result.icon5 = accentColor;
    result.border6 = accentColor;
    result.background5 = Color(accentColor).alpha(0.3).toString();
    result.background6 = Color(accentColor).alpha(0.1).toString();

    result.button = {
      ...result.button,
      border2: accentColor,
      fill2: accentColor,
      hover2: Color(accentColor).lighten(0.2).toString(),
      pressed2: Color(accentColor).lighten(0.2).toString(),
    };

    result.lostColors = {
      ...result.lostColors,
      blueShrimp: Color(accentColor).lighten(0.2).toString(),
    };
  }

  return result;
};

export type ColorsV2 = typeof lightV2;

/**
 * @deprecated
 */
const lightV2 = {
  b2b: '#0D585C',
  background1: '#FFFFFF',
  background2: '#F1F5F6',
  background3: '#C9F7EA',
  background4: '#F0FCF9',
  background5: '#DCEAFA',
  background6: '#EBF2FA',
  background7: '#F1F5F6',
  background8: '#25D2A2',
  background9: '#E5ECF0',
  background10: '#819AA9',
  background12: '#E5ECF0',
  background13: '#87B9E8',
  background14: '#D9D9D9',
  background15: '#E9F9F4',
  background16: '#25D2A2',
  background17: '#1F313C',
  backgroundGrayLight: '#F1F5F6',
  blue10: '#CEE5FF',
  border1: '#F1F5F6',
  border2: '#E5ECF0',
  border3: '#25D2A2',
  border4: '#FA5353',
  border5: '#819AA9',
  border6: '#559DEE',
  button: {
    border1: '#25D2A2',
    border2: '#559DEE',
    border3: '#819AA9',
    fill1: '#25D2A2',
    fill2: '#559DEE',
    fill3: '#F1F5F6',
    fill4: '#C9F7EA',
    fill5: '#DCEAFA',
    fill6: '#FCECE4',
    fill7: '#EEEAFB',
    hover1: '#45DFB4',
    hover2: '#7BB5F6',
    pressed1: '#2DB48E',
    pressed2: '#376DAA',
  },
  core: '#25D2A2',
  error: '#FA5353',
  gamification1: '#E5EBFA',
  gamification2: '#0BC2E1',
  global1: '#25D2A2',
  global2: '#559DEE',
  global3: '#000000',
  global4: '#FFFFFF',
  global5: '#295384',
  goal: {
    goal1: '#DFB998',
    goal2: '#F8C081',
    goal3: '#FFA06A',
    goal4: '#ED7D7D',
    goal5: '#72DDB7',
    goal6: '#9BB6B5',
    goal7: '#75B4B4',
    goal8: '#ADC3D4',
    goal9: '#87B9E8',
    goal10: '#57BAD3',
    goal11: '#659CB9',
  },
  gray10: '#F1F5F6',
  icon1: '#000000',
  icon2: '#FFFFFF',
  icon3: '#819AA9',
  icon4: '#25D2A2',
  icon5: '#559DEE',
  icon6: '#FFA06A',
  icon7: '#886DDE',
  lostColors: {
    blueShrimp: '#4A8DDA',
    chatBadge1: '#FFE976',
    errorLight: '#F2E5E6',
    greenElephant: '#2BC198',
    pageBg: '#F6F9FA',
    sashaGray: '#C0CCD4',
    tooltipGray: '#9AAEBA',
  },
  note: {
    note1: '#FFFFFF',
    note2: '#C9F7EA',
    note3: '#DCEAFA',
    note4: '#FCECE4',
    note5: '#EEEAFB',
  },
  onboarding: {
    onboarding1: '#EDC829',
  },
  shadow1: '#9BEED7',
  shadow2: '#B5D1F0',
  support: '#559DEE',

  text1: '#000000',

  text2: '#FFFFFF',
  text3: '#819AA9',

  text4: '#25D2A2',
  text5: '#559DEE',

  text6: '#295384',
  warning1: '#FFA06A',
};

/**
 * @deprecated
 */
const darkV2 = {
  ...lightV2,

  background1: '#14171B',

  background2: '#202326',
  background3: '#162F2C',
  background4: '#173C35',
  background5: '#1C2837',
  background6: '#203145',
  background7: '#202326',
  background8: '#1FB088',
  background9: '#33383E',
  background14: '#D9D9D9',
  background15: '#E9F9F4',
  background16: '#162F2C',
  background17: '#293336',
  border1: '#202326',

  border2: '#33383E',
  border3: '#1FB088',
  border4: '#D24646',
  border5: '#7A8086',
  border6: '#4784C8',
  button: {
    ...lightV2.button,

    border1: '#1FB088',
    border2: '#4989D2',
    border3: '#7A8086',
    fill1: '#1FB088',
    fill2: '#4784C8',
    fill3: '#202326',
    fill4: '#162F2C',

    fill5: '#1C2837',
    fill6: '#322D28',
    fill7: '#27253A',
  },

  core: '#1FB088',
  error: '#D24646',
  global1: '#1FB088',
  global2: '#4784C8',
  global3: '#FFFFFF',

  global4: '#000000',
  icon1: '#F5F9FA',
  icon2: '#000000',
  icon3: '#7A8086',
  icon4: '#1FB088',
  icon5: '#4784C8',
  icon6: '#CC8055',

  icon7: '#7861C4',

  shadow1: '#245A4B',

  shadow2: '#1A3A5E',
  support: '#4784C8',
  text1: '#F5F9FA',
  text2: '#000000',

  text3: '#7A8086',

  text4: '#1FB088',
  text5: '#4784C8',

  warning1: '#FF8C00',
};
